
import React, { useState, useEffect } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, Search, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { CampaignStatistics, UserViewData } from '@/types/modalCampaigns';
import { getCampaignStatistics } from '@/services/modalCampaigns/campaignStats';
import LineChartDisplay from '@/components/reports/charts/LineChartDisplay';
import PieChartDisplay from '@/components/reports/charts/PieChartDisplay';
import BarChartDisplay from '@/components/reports/charts/BarChartDisplay';

interface CampaignStatsProps {
  campaignId: string;
  onBack: () => void;
}

const CampaignStats: React.FC<CampaignStatsProps> = ({ campaignId, onBack }) => {
  const [stats, setStats] = useState<CampaignStatistics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [dateRange, setDateRange] = useState<number>(30); // Default to 30 days

  useEffect(() => {
    loadStatistics();
  }, [campaignId]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const data = await getCampaignStatistics(campaignId);
      setStats(data);
    } catch (error) {
      console.error('Error loading campaign statistics:', error);
      toast.error('Er is een fout opgetreden bij het laden van de statistieken');
    } finally {
      setLoading(false);
    }
  };

  const exportToCsv = () => {
    if (!stats) return;
    
    // Create CSV headers
    const headers = [
      'Gebruiker ID',
      'Gebruikersnaam',
      'Aantal weergaven',
      'Laatst bekeken',
      'Gesloten',
      'Permanent gesloten',
      'Quiz pogingen',
      'Quiz gehaald',
      'Hoogste score'
    ].join(',');
    
    // Create CSV rows
    const rows = stats.userViewData.map(user => [
      user.userId,
      user.userName,
      user.viewCount,
      user.lastViewed || '',
      user.dismissed ? 'Ja' : 'Nee',
      user.dismissedPermanently ? 'Ja' : 'Nee',
      user.quizAttempts,
      user.quizPassed ? 'Ja' : 'Nee',
      user.highestScore !== null ? user.highestScore : ''
    ].join(','));
    
    // Combine headers and rows
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows.join('\n')}`;
    
    // Create download link and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `campaign_stats_${stats.campaignTitle}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Filter users based on search term
  const filteredUsers = stats?.userViewData.filter(user => 
    user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Prepare data for charts
  const prepareViewsChartData = () => {
    if (!stats) return [];
    
    // Filter data based on selected date range
    const cutoffDate = subDays(new Date(), dateRange).toISOString().split('T')[0];
    
    return stats.dailyViewData
      .filter(item => item.date >= cutoffDate)
      .map(item => ({
        name: format(parseISO(item.date), 'dd MMM', { locale: nl }),
        value: item.count,
        date: item.date
      }));
  };

  const preparePieChartData = () => {
    if (!stats) return [];
    
    return [
      { name: 'Bekeken en niet gesloten', value: stats.totalViews - (stats.totalViews * stats.dismissalRate) },
      { name: 'Normaal gesloten', value: (stats.totalViews * stats.dismissalRate) - (stats.totalViews * stats.permanentDismissalRate) },
      { name: 'Permanent gesloten', value: stats.totalViews * stats.permanentDismissalRate }
    ];
  };

  const prepareQuizChartData = () => {
    if (!stats || !stats.hasQuiz) return [];
    
    return [
      { name: 'Geslaagd', value: stats.totalQuizAttempts * stats.passRate },
      { name: 'Niet geslaagd', value: stats.totalQuizAttempts * (1 - stats.passRate) }
    ];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Statistieken laden...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-muted-foreground mb-4">Geen statistieken gevonden</p>
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{stats.campaignTitle}</h2>
          <p className="text-muted-foreground">Statistieken</p>
        </div>
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="views">Weergaven</TabsTrigger>
          {stats.hasQuiz && (
            <TabsTrigger value="quiz">Quiz resultaten</TabsTrigger>
          )}
          <TabsTrigger value="users">Gebruikers</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-medium">Totale weergaven</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.totalViews}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-medium">Unieke gebruikers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{stats.uniqueViewers}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-medium">Sluitingspercentage</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{(stats.dismissalRate * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-medium">Permanent gesloten</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{(stats.permanentDismissalRate * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
          </div>

          {stats.hasQuiz && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-sm font-medium">Quiz pogingen</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats.totalQuizAttempts}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-sm font-medium">Slagingspercentage</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{(stats.passRate * 100).toFixed(1)}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-4">
                  <CardTitle className="text-sm font-medium">Gemiddelde score</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats.averageScore.toFixed(1)}</p>
                </CardContent>
              </Card>
            </div>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Weergaven over tijd</CardTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor="dateRange">Periode:</Label>
                <select
                  id="dateRange"
                  value={dateRange}
                  onChange={e => setDateRange(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={7}>Afgelopen 7 dagen</option>
                  <option value={30}>Afgelopen 30 dagen</option>
                  <option value={90}>Afgelopen 3 maanden</option>
                  <option value={365}>Afgelopen jaar</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <LineChartDisplay
                data={prepareViewsChartData()}
                dataKey="value"
                nameKey="name"
                lineName="Aantal weergaven"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="views" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Weergaven per dag</CardTitle>
              </CardHeader>
              <CardContent>
                <LineChartDisplay
                  data={prepareViewsChartData()}
                  dataKey="value"
                  nameKey="name"
                  lineName="Aantal weergaven"
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Interactie met modals</CardTitle>
              </CardHeader>
              <CardContent>
                <PieChartDisplay
                  data={preparePieChartData()}
                  height={300}
                />
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Weergaven per dag</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChartDisplay
                data={prepareViewsChartData()}
                dataKey="value"
                nameKey="name"
                barName="Aantal weergaven"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {stats.hasQuiz && (
          <TabsContent value="quiz" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quiz resultaten</CardTitle>
                </CardHeader>
                <CardContent>
                  <PieChartDisplay
                    data={prepareQuizChartData()}
                    height={300}
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Quiz statistieken</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <dl className="divide-y divide-gray-200">
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Totaal aantal pogingen</dt>
                      <dd className="text-sm font-semibold">{stats.totalQuizAttempts}</dd>
                    </div>
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Unieke deelnemers</dt>
                      <dd className="text-sm font-semibold">{stats.uniqueQuizParticipants}</dd>
                    </div>
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Geslaagde pogingen</dt>
                      <dd className="text-sm font-semibold">{Math.round(stats.totalQuizAttempts * stats.passRate)}</dd>
                    </div>
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Slagingspercentage</dt>
                      <dd className="text-sm font-semibold">{(stats.passRate * 100).toFixed(1)}%</dd>
                    </div>
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Gemiddelde score</dt>
                      <dd className="text-sm font-semibold">{stats.averageScore.toFixed(1)} punten</dd>
                    </div>
                    <div className="py-4 flex justify-between">
                      <dt className="text-sm font-medium text-muted-foreground">Pogingen per deelnemer</dt>
                      <dd className="text-sm font-semibold">
                        {stats.uniqueQuizParticipants > 0 
                          ? (stats.totalQuizAttempts / stats.uniqueQuizParticipants).toFixed(1) 
                          : '0'}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gebruikersstatistieken</CardTitle>
              <div className="flex items-center justify-between">
                <div className="relative max-w-sm">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Zoek op naam of ID"
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <Button variant="outline" onClick={exportToCsv}>
                  <Download className="h-4 w-4 mr-2" />
                  Exporteer als CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gebruiker</TableHead>
                      <TableHead>Weergaven</TableHead>
                      <TableHead>Laatste weergave</TableHead>
                      <TableHead>Status</TableHead>
                      {stats.hasQuiz && (
                        <>
                          <TableHead>Quiz pogingen</TableHead>
                          <TableHead>Resultaat</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <TableRow key={user.userId}>
                          <TableCell>
                            <div className="font-medium">{user.userName}</div>
                            <div className="text-xs text-muted-foreground">{user.userId}</div>
                          </TableCell>
                          <TableCell>{user.viewCount}</TableCell>
                          <TableCell>
                            {user.lastViewed 
                              ? format(parseISO(user.lastViewed), 'dd-MM-yyyy HH:mm', { locale: nl })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {user.dismissedPermanently ? (
                              <Badge variant="destructive">Permanent gesloten</Badge>
                            ) : user.dismissed ? (
                              <Badge variant="secondary">Gesloten</Badge>
                            ) : (
                              <Badge variant="default" className="bg-green-500">Actief</Badge>
                            )}
                          </TableCell>
                          {stats.hasQuiz && (
                            <>
                              <TableCell>{user.quizAttempts}</TableCell>
                              <TableCell>
                                {user.quizAttempts > 0 ? (
                                  user.quizPassed ? (
                                    <Badge variant="default" className="bg-green-500">
                                      Geslaagd ({user.highestScore})
                                    </Badge>
                                  ) : (
                                    <Badge variant="destructive">
                                      Niet geslaagd ({user.highestScore})
                                    </Badge>
                                  )
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={stats.hasQuiz ? 6 : 4} className="text-center py-6">
                          Geen gebruikers gevonden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignStats;
